/*
  guide-core.js — shared behaviour for the product guide family
  (shorts guide, sports bra guide; leggings guide to follow).

  Boots on every [data-guide-root]. Pairs with snippets/guide-core.liquid.

  1. Reveal: GSAP + ScrollTrigger + Flip when loaded (hero timeline, parallax,
     batched .rv reveals), IntersectionObserver fallback, reduced-motion safe.
  2. Fit finder: every .chips[data-group="x"] filters the cards in
     [data-guide-grid] on their data-x attribute. data-guide-grid="filter"
     dims non-matches in place instead of moving matches to the front. Card values are
     space-separated and any match counts. One chip per group, click again to
     clear. Matches move to the front (Flip animates when available).
     [data-guide-count] and [data-guide-clear] are wired if present.
  3. Size table: [data-guide-sizes] is filled from the JSON in
     <script type="application/json" data-guide-size-data> and toggles cm/in
     from .seg button[data-unit].

  window.MNGuide.init(root) is exposed for sections that render after load.
*/
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.guideInit) return;
    root.dataset.guideInit = '1';

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var rv = Array.prototype.slice.call(root.querySelectorAll('.rv'));
    var GS = !reduce && window.gsap && window.ScrollTrigger && window.Flip;

    /* ---- reveal ---- */
    if (GS) {
      gsap.registerPlugin(ScrollTrigger, Flip);
      rv.forEach(function (el) { el.style.transition = 'none'; });
      if (rv.length) {
        ScrollTrigger.batch(rv, {
          start: 'top 88%',
          once: true,
          onEnter: function (batch) {
            gsap.to(batch, {
              autoAlpha: 1, y: 0, duration: .6, stagger: .08, ease: 'power2.out', overwrite: true,
              /* hand the revealed state back to CSS (.rv.in) so .dim and :hover can still win */
              onComplete: function () {
                batch.forEach(function (el) { el.classList.add('in'); el.style.transition = ''; });
                gsap.set(batch, { clearProps: 'opacity,visibility,transform' });
              }
            });
          }
        });
      }
      var hero = root.querySelector('.hero');
      if (hero) {
        var img = hero.querySelector('.bg img');
        var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        if (img) tl.from(img, { scale: 1.1, duration: 1.2, ease: 'power2.out' }, 0);
        [['.crumb', { y: 16, autoAlpha: 0, duration: .4 }, .1], ['h1', { y: 40, autoAlpha: 0, duration: .7 }, '-=.2'], ['p', { y: 22, autoAlpha: 0, duration: .5 }, '-=.35'], ['.cta', { y: 16, autoAlpha: 0, duration: .4 }, '-=.25']]
          .forEach(function (s) { var el = hero.querySelector(s[0]); if (el) tl.from(el, s[1], s[2]); });
        if (img) gsap.to(img, { yPercent: 8, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: .5 } });
      }
    } else if (reduce || !('IntersectionObserver' in window)) {
      rv.forEach(function (el) { el.classList.add('in'); });
    } else {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
      }, { threshold: .05, rootMargin: '9999px 0px -6% 0px' });
      rv.forEach(function (el) { io.observe(el); });
    }

    /* ---- fit finder ---- */
    var grid = root.querySelector('[data-guide-grid]');
    var groups = Array.prototype.slice.call(root.querySelectorAll('.chips[data-group]'));
    if (grid && groups.length) {
      var cards = Array.prototype.slice.call(grid.querySelectorAll('.style-card'));
      var baseOrder = cards.slice();
      /* data-guide-grid="filter": dim only, never move cards (tiers keep their sections) */
      var reorder = grid.getAttribute('data-guide-grid') !== 'filter';
      var count = root.querySelector('[data-guide-count]');
      var clear = root.querySelector('[data-guide-clear]');
      var state = {};
      groups.forEach(function (g) { state[g.dataset.group] = null; });

      function active() { return Object.keys(state).some(function (k) { return !!state[k]; }); }
      function matches(card) {
        return Object.keys(state).every(function (k) {
          if (!state[k]) return true;
          var v = (card.getAttribute('data-' + k) || '').split(/\s+/);
          return v.indexOf(state[k]) > -1;
        });
      }
      function apply() {
        var filtered = active();
        var flipState = (GS && reorder) ? Flip.getState(cards) : null;
        var matched = [], dimmed = [];
        cards.forEach(function (c) {
          var ok = matches(c);
          c.classList.toggle('dim', !ok);
          (ok ? matched : dimmed).push(c);
        });
        if (reorder) {
          (filtered ? matched.concat(dimmed) : baseOrder).forEach(function (c) { grid.appendChild(c); });
          if (flipState) Flip.from(flipState, { duration: .55, ease: 'power2.inOut', stagger: .02 });
          /* cards moved to the front may not have revealed yet; show them and re-measure triggers */
          cards.forEach(function (c) { if (!c.classList.contains('in')) { c.classList.add('in'); c.style.transition = ''; if (GS) gsap.set(c, { clearProps: 'opacity,visibility,transform' }); } });
          if (GS) ScrollTrigger.refresh();
        }
        if (count) {
          count.textContent = filtered
            ? ('Showing ' + matched.length + ' of ' + cards.length + ' styles')
            : ('Showing all ' + cards.length + ' styles');
        }
        if (clear) clear.hidden = !filtered;
      }
      groups.forEach(function (group) {
        var key = group.dataset.group;
        group.addEventListener('click', function (ev) {
          var btn = ev.target.closest('.chip');
          if (!btn || !group.contains(btn)) return;
          var on = btn.getAttribute('aria-pressed') === 'true';
          group.querySelectorAll('.chip').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
          if (!on) btn.setAttribute('aria-pressed', 'true');
          state[key] = on ? null : btn.dataset.val;
          apply();
        });
      });
      if (clear) {
        clear.addEventListener('click', function () {
          Object.keys(state).forEach(function (k) { state[k] = null; });
          root.querySelectorAll('.chips[data-group] .chip').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
          apply();
        });
      }
    }

    /* ---- size table, cm as source of truth ---- */
    var table = root.querySelector('[data-guide-sizes]');
    var dataEl = root.querySelector('[data-guide-size-data]');
    if (table && dataEl) {
      var data = null;
      try { data = JSON.parse(dataEl.textContent); } catch (e) { data = null; }
      if (data && data.rows && data.cols) {
        /* US visitors start on inches. A pick is kept in localStorage (mn-unit),
           shared with the size guide and the leggings guide. */
        var UNIT = (function () {
          try { var u = localStorage.getItem('mn-unit'); if (u === 'cm' || u === 'in') return u; } catch (e) {}
          return window.MN && MN.shopify && MN.shopify.country === 'US' ? 'in' : 'cm';
        })();
        var cv = function (v) { return UNIT === 'in' ? Math.round((v / 2.54) * 2) / 2 : v; };
        var range = function (a) { return Array.isArray(a) ? (cv(a[0]) + '-' + cv(a[1])) : cv(a); };
        var render = function () {
          table.querySelector('tbody').innerHTML = data.rows.map(function (r) {
            var cells = data.cols.map(function (c) { return '<td>' + range(r[c.key]) + '</td>'; }).join('');
            return '<tr><th>' + r.s + (r.au ? '<span class="au">AU ' + r.au + '</span>' : '') + '</th>' + cells + '</tr>';
          }).join('');
          table.querySelectorAll('thead .u').forEach(function (u) { u.textContent = UNIT; });
        };
        root.querySelectorAll('.seg button[data-unit]').forEach(function (b) {
          b.addEventListener('click', function () {
            UNIT = b.dataset.unit;
            root.querySelectorAll('.seg button[data-unit]').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
            render();
            try { localStorage.setItem('mn-unit', UNIT); } catch (e) {}
          });
        });
        root.querySelectorAll('.seg button[data-unit]').forEach(function (x) { x.setAttribute('aria-pressed', String(x.dataset.unit === UNIT)); });
        render();
      }
    }
  }

  function boot() {
    document.querySelectorAll('[data-guide-root]').forEach(init);
  }

  window.MNGuide = window.MNGuide || {};
  window.MNGuide.init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* theme editor: sections re-render without a page load */
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target && e.target.querySelector ? e.target.querySelector('[data-guide-root]') : null;
    if (r) init(r);
  });
})();
